import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, CircularProgress, IconButton, Tooltip, Collapse
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import {
  fetchLibraryItems,
  createLibraryItem,
  updateLibraryItem,
  deleteLibraryItem
} from '../../slices/librarySlice';
import {
  C, FONT, inputSx, primaryButtonSx, dialogPaperProps, dialogTitleSx,
  TileGrid, BookCover, FilterChips, EmptyState
} from './libraryUi';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ITEM_TYPES = [
  { value: 'book', label: 'كتاب' },
  { value: 'magazine', label: 'مجلة' },
  { value: 'resource', label: 'مصدر' },
  { value: 'other', label: 'أخرى' },
];

const LANGUAGES = [
  { value: 'ar', label: 'عربي' },
  { value: 'en', label: 'إنجليزي' },
  { value: 'fr', label: 'فرنسي' },
  { value: 'other', label: 'أخرى' },
];

const EMPTY_FORM = {
  title: '',
  author: '',
  isbn: '',
  item_type: 'book',
  category: '',
  language: 'ar',
  shelf: '',
  publish_year: '',
  publisher: '',
  number_of_copies: 1
};

// DRF returns either { error } or field errors like { number_of_copies: ['...'] }
const getErrorMessage = (error) => {
  if (!error || typeof error !== 'object') return 'حدث خطأ';
  if (error.error) return error.error;
  const firstField = Object.values(error)[0];
  return Array.isArray(firstField) ? firstField[0] : 'حدث خطأ';
};

const BookCard = ({ item, onEdit, onDelete }) => {
  const total = item.copies_count || 0;
  const available = item.available_copies_count || 0;
  const tone = available === 0 ? C.danger : available === total ? C.success : C.warning;

  return (
    <Box
      sx={{
        display: 'flex', gap: 2, p: 2, borderRadius: 4, bgcolor: '#fff',
        border: `1px solid ${C.border}`,
        transition: 'transform .15s, box-shadow .15s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 10px 24px rgba(67,56,202,0.10)' },
        '&:hover .book-actions': { opacity: 1 },
      }}
    >
      <BookCover title={item.title} width={64} />
      <Box flex={1} minWidth={0} display="flex" flexDirection="column">
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          <Typography fontWeight={800} color={C.text} sx={{ fontFamily: FONT, flex: 1, lineHeight: 1.4 }}>
            {item.title}
          </Typography>
          <Box className="book-actions" sx={{ display: 'flex', opacity: { xs: 1, md: 0 }, transition: 'opacity .15s' }}>
            <Tooltip title="تعديل">
              <IconButton size="small" onClick={() => onEdit(item)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="حذف">
              <IconButton size="small" onClick={() => onDelete(item.id)} sx={{ '&:hover': { color: C.danger } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="body2" color={C.muted} noWrap sx={{ fontFamily: FONT }}>
          {item.author || 'مؤلف غير محدد'}
          {item.shelf ? ` · رف ${item.shelf}` : ''}
        </Typography>

        <Box mt="auto" pt={1.5}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight={700} sx={{ fontFamily: FONT, color: tone }}>
              {available === 0 ? 'كل النسخ مستعارة' : `${available} متاح`}
            </Typography>
            <Typography variant="caption" color={C.muted} sx={{ fontFamily: FONT }}>
              {total} نسخة
            </Typography>
          </Box>
          <Box sx={{ height: 6, borderRadius: 999, bgcolor: '#F3F4F6', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: total ? `${(available / total) * 100}%` : 0, bgcolor: tone, borderRadius: 999 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const LibraryCatalogTab = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, loading, saving } = useSelector((state) => state.library);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    dispatch(fetchLibraryItems());
  }, [dispatch]);

  const setField = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || '',
        author: item.author || '',
        isbn: item.isbn || '',
        item_type: item.item_type || 'book',
        category: item.category || '',
        language: item.language || 'ar',
        shelf: item.shelf || '',
        publish_year: item.publish_year || '',
        publisher: item.publisher || '',
        number_of_copies: item.copies_count ?? 1
      });
    } else {
      setEditingItem(null);
      setFormData(EMPTY_FORM);
    }
    setShowMore(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    const data = { ...formData, number_of_copies: Number(formData.number_of_copies) || 0 };
    try {
      if (editingItem) {
        await dispatch(updateLibraryItem({ id: editingItem.id, ...data })).unwrap();
        showToast('تم تحديث الكتاب بنجاح', 'success');
      } else {
        await dispatch(createLibraryItem(data)).unwrap();
        showToast('تمت إضافة الكتاب بنجاح', 'success');
      }
      handleCloseDialog();
      dispatch(fetchLibraryItems());
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الكتاب؟')) {
      try {
        await dispatch(deleteLibraryItem(id)).unwrap();
        showToast('تم حذف الكتاب بنجاح', 'success');
        dispatch(fetchLibraryItems());
      } catch (error) {
        showToast(getErrorMessage(error), 'error');
      }
    }
  };

  const typeOptions = useMemo(() => [
    { value: 'all', label: 'الكل', count: items.length },
    ...ITEM_TYPES
      .map((t) => ({ ...t, count: items.filter((i) => i.item_type === t.value).length }))
      .filter((t) => t.count > 0),
  ], [items]);

  const filteredItems = items.filter((item) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      item.title?.toLowerCase().includes(term) ||
      item.author?.toLowerCase().includes(term) ||
      item.isbn?.toLowerCase().includes(term);
    const matchesType = filterType === 'all' || item.item_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading && items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  const addButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} sx={primaryButtonSx}>
      إضافة كتاب
    </Button>
  );

  return (
    <Box>
      {items.length === 0 ? (
        <EmptyState
          icon={<AutoStoriesIcon />}
          title="لا توجد كتب بعد"
          hint="أضف أول كتاب وحدد عدد النسخ المتوفرة منه"
          action={addButton}
        />
      ) : (
        <>
          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              placeholder="ابحث بالعنوان أو المؤلف أو ISBN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: C.muted }} />
                  </InputAdornment>
                )
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 220 }}
            />
            {addButton}
          </Box>
          {typeOptions.length > 2 && (
            <Box mb={2.5}>
              <FilterChips options={typeOptions} value={filterType} onChange={setFilterType} />
            </Box>
          )}

          {filteredItems.length === 0 ? (
            <EmptyState icon={<SearchIcon />} title="لا توجد نتائج" hint="جرّب كلمة بحث أخرى" />
          ) : (
            <TileGrid min={300}>
              {filteredItems.map((item) => (
                <BookCard key={item.id} item={item} onEdit={handleOpenDialog} onDelete={handleDelete} />
              ))}
            </TileGrid>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={dialogPaperProps}>
        <DialogTitle sx={dialogTitleSx}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <BookCover title={formData.title || 'كتاب'} width={36} />
            {editingItem ? 'تعديل الكتاب' : 'إضافة كتاب جديد'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              label="العنوان"
              value={formData.title}
              onChange={setField('title')}
              required
              autoFocus
              sx={{ ...inputSx, gridColumn: '1 / -1' }}
            />
            <TextField
              label="عدد النسخ"
              type="number"
              value={formData.number_of_copies}
              onChange={setField('number_of_copies')}
              inputProps={{ min: 0 }}
              helperText={editingItem ? `المستعار حالياً: ${(editingItem.copies_count || 0) - (editingItem.available_copies_count || 0)}` : ''}
              sx={inputSx}
            />
            <TextField
              label="المؤلف"
              value={formData.author}
              onChange={setField('author')}
              sx={inputSx}
            />
          </Box>

          <Button
            onClick={() => setShowMore(!showMore)}
            endIcon={<ExpandMoreIcon sx={{ transform: showMore ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
            sx={{ mt: 2, fontFamily: FONT, fontWeight: 700, color: C.primary }}
          >
            تفاصيل إضافية
          </Button>
          <Collapse in={showMore}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mt: 1.5,
              }}
            >
              <TextField select label="النوع" value={formData.item_type} onChange={setField('item_type')} sx={inputSx}>
                {ITEM_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              <TextField select label="اللغة" value={formData.language} onChange={setField('language')} sx={inputSx}>
                {LANGUAGES.map((l) => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
              </TextField>
              <TextField label="التصنيف" value={formData.category} onChange={setField('category')} sx={inputSx} />
              <TextField label="الرف/الموقع" value={formData.shelf} onChange={setField('shelf')} sx={inputSx} />
              <TextField label="ISBN" value={formData.isbn} onChange={setField('isbn')} sx={inputSx} />
              <TextField label="سنة النشر" type="number" value={formData.publish_year} onChange={setField('publish_year')} sx={inputSx} />
              <TextField
                label="الناشر"
                value={formData.publisher}
                onChange={setField('publisher')}
                sx={{ ...inputSx, gridColumn: '1 / -1' }}
              />
            </Box>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseDialog} sx={{ fontFamily: FONT, color: C.muted }}>إلغاء</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.title} sx={primaryButtonSx}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LibraryCatalogTab;
